// LmsPage.jsx
import React, { useState, useEffect } from "react";
import PageBlock from "./PageBlock";
import axios from "axios";
import * as constants from "../constants";
import { useLocation, useParams } from "react-router-dom";
import queryString from "query-string";
import { BookOpen, FileText, AlertCircle, Layers } from "lucide-react";

// Loading Skeleton - matches ProgramSummary style
const LoadingSkeleton = () => (
  <div className="animate-pulse space-y-6">
    <div className="bg-white rounded-md p-4 sm:p-6 lg:p-8 border border-gray-200">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="h-7 w-32 bg-gray-200 rounded-full"></div>
      </div>
      <div className="h-6 w-2/3 bg-gray-200 rounded mb-3"></div>
      <div className="h-4 w-full bg-gray-200 rounded mb-2"></div>
      <div className="h-4 w-3/4 bg-gray-200 rounded"></div>
    </div>
    <div className="bg-white rounded-xl p-4 sm:p-6 border border-gray-200">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-9 h-9 bg-gray-200 rounded-lg"></div>
        <div>
          <div className="h-5 w-24 bg-gray-200 rounded mb-1"></div>
          <div className="h-3 w-16 bg-gray-200 rounded"></div>
        </div>
      </div>
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-20 bg-gray-100 rounded-lg"></div>
        ))}
      </div>
    </div>
  </div>
);

// Empty State Component
const EmptyState = () => (
  <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-md">
    <div className="flex flex-col items-center justify-center py-12 px-4">
      <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mb-3">
        <AlertCircle className="w-7 h-7 text-gray-400" />
      </div>
      <h3 className="text-base font-semibold text-gray-900 mb-1">
        No Information Found
      </h3>
      <p className="text-sm text-gray-500 text-center max-w-md">
        The content you're looking for is not available at the moment.
      </p>
    </div>
  </div>
);

const LmsPage = () => {
  const [lmsPage, setLmsPage] = useState(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const location = useLocation();
  const { id } = useParams();
  const { objectType } = queryString.parse(location.search);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await axios.get(
          `${constants.BASEURL}LmsPage/0?objectId=${id}&objectType=${objectType}`
        );
        setLmsPage(response.data);
      } catch (e) {
        console.log(e);
        setError("Failed to load content");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [location?.search, id, objectType]);

  const isEmpty =
    !lmsPage ||
    lmsPage === "" ||
    (typeof lmsPage === "object" && Object.keys(lmsPage).length === 0);

  if (loading) {
    return <LoadingSkeleton />;
  }

  if (error || isEmpty) {
    return <EmptyState />;
  }

  return (
    <div className="px-4 py-4">
      {/* Header Card - matches ProgramSummary style */}
      <div className="bg-white rounded-md border border-gray-200 p-4 sm:p-6 lg:p-8 shadow-md space-y-6">
        <div className="w-full max-w-6xl space-y-6">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          {/* Left - Page Info */}
          <div className="flex-1 min-w-0">
            {/* Badge */}
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-indigo-50 text-indigo-700 text-sm font-medium rounded-full">
                <BookOpen className="w-4 h-4 shrink-0" />
                Course Outline
              </span>
              {lmsPage.lmsPageBlocks?.length > 0 && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-gray-100 text-gray-600 text-sm rounded-full">
                  <Layers className="w-3.5 h-3.5" />
                  {lmsPage.lmsPageBlocks.length} section
                  {lmsPage.lmsPageBlocks.length !== 1 ? "s" : ""}
                </span>
              )}
            </div>

            {/* Page Name */}
            <h2 className="text-xl font-semibold text-gray-900 mb-2 break-words sm:truncate">
              {lmsPage.lmspageName || "Course Content"}
            </h2>

            {/* Description */}
            {lmsPage.lmspageDescription && (
              <p className="text-sm text-gray-600 leading-relaxed">
                {lmsPage.lmspageDescription}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Content Blocks Section */}
      {lmsPage.lmsPageBlocks?.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 shadow-md min-w-0">
          <div className="flex items-start sm:items-center gap-3 mb-4 min-w-0">
            <div className="w-9 h-9 bg-blue-50 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-blue-600" />
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold text-gray-900">Content</h3>
              <p className="text-xs text-gray-500">Course materials and information</p>
            </div>
          </div>

          <div className="space-y-4 w-full min-w-0 overflow-hidden">
            {lmsPage.lmsPageBlocks?.map((pageBlock, index) => (
              <PageBlock
                key={pageBlock.lmspageBlockId}
                blockContent={pageBlock}
                index={index}
              />
            ))}
          </div>
        </div>
      )}
    </div>
    </div>
  );
};

export default LmsPage;